import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AESDecryptURL } from "utils/encryption/AESDecrypt";

// REDUX
import {
  FormatCurrency,
  NoRecordField,
  setTitleCase,
} from "app/components/template/partials";
import { STATUS } from "configs/constant";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { notifyCustomer } from "utils/action";
import { AESEncryptURL } from "utils/encryption/AESEncrypt";
import { getCustomerByID } from "../../../redux/customer";

// HOOKS
import useAuth from "app/hooks/useAuth";

export function CustomerDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { isStrictAccess } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const { getCustomerByIDData } = useSelector(
    (state) => ({
      getCustomerByIDData: state.GetCustomerByIDData,
    }),
    shallowEqual
  );
  const { data } = getCustomerByIDData;
  const scheduleData = location.state?.scheduleData ?? null;
  const decID = parseInt(AESDecryptURL(id));

  const onBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    dispatch(getCustomerByID(decID));
  }, [dispatch, decID]);

  const handleFormSubmit = async (status) => {
    setLoading(true);
    const request = {
      status: status,
    };
    // send to backend
    try {
      const response = await notifyCustomer(decID, request);
      window.open(response.data?.redirect, "_blank");
      toast.success(setTitleCase("berhasil mengirim notifikasi ke pelanggan"));
    } catch (error) {
      toast.error(setTitleCase(error.response.data?.message));
    }
    setLoading(false);
  };

  const handleConfirmDoneDialog = (
    customerDataSelected,
    scheduleDataSelected
  ) => {
    const secureID = AESEncryptURL(customerDataSelected?.id.toString());
    navigate(`/customer/${secureID}/confirm-done`, {
      state: {
        showDialog: true,
        customerData: data,
        scheduleData: scheduleDataSelected,
      },
    });
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="lg">
        <Card className="card-container">
          <CardHeader
            title="  Detail Pelanggan"
            action={
              <Tooltip title="Kembali" arrow>
                <IconButton aria-label="back-menu" onClick={onBack}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent className="card-content-column">
            <Grid container spacing={2}>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">NIK</Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.nik)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  NAMA LENGKAP
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.nama_pelanggan)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  NAMA USER
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.nama_user)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  NO.TELEPON
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.nomor_hp)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  Waktu Penyedotan
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {data?.waktu_pembersihan
                    ? dayjs(data.waktu_pembersihan)
                        .locale("id")
                        .format("dddd, DD MMMM YYYY [Pukul] HH:mm")
                    : NoRecordField(data?.waktu_pembersihan)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  Waktu Penyedotan Selanjutnya
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {data?.waktu_pembersihan_selanjutnya
                    ? dayjs(data?.waktu_pembersihan_selanjutnya)
                        .locale("id")
                        .format("dddd, DD MMMM YYYY [Pukul] HH:mm")
                    : NoRecordField(data?.waktu_pembersihan_selanjutnya)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  TAGIHAN
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {FormatCurrency(data?.tagihan)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  JUMLAH PENGHUNI
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.jumlah_penghuni)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  Volume Tangki
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.volume_tangki_septik)} m³
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  JENIS BANGUNAN
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.jenis_bangunan)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  KELURAHAN
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.kelurahan)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  KECAMATAN
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.kecamatan)}
                </Typography>
                <Divider />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography className="card-content-key-text">
                  ALAMAT
                </Typography>
                <Typography gutterBottom className="card-content-value-text">
                  {NoRecordField(data?.alamat_detail)}
                </Typography>
                <Divider />
              </Grid>
            </Grid>
          </CardContent>
          {isStrictAccess && scheduleData && (
            <CardActions className="card-content-row">
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={() =>
                      handleFormSubmit(STATUS.DONE_TAKING_SCHEDULE)
                    }
                    loading={isLoading}
                  >
                    Notifikasi Sudah Mengambil Jadwal
                  </LoadingButton>
                </Grid>
                <Grid item md={4} xs={12}>
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={() => handleFormSubmit(STATUS.NOTIFICATION_H1)}
                    loading={isLoading}
                  >
                    Notifikasi H-1 Penyedotan
                  </LoadingButton>
                </Grid>
                <Grid item md={4} xs={12}>
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={() => handleConfirmDoneDialog(data, scheduleData)}
                    loading={isLoading}
                  >
                    Notifikasi Telah Selesai Dibersihkan
                  </LoadingButton>
                </Grid>
              </Grid>
            </CardActions>
          )}
        </Card>
      </Container>
    </Box>
  );
}
